import { API_PREFIX, paginatedFromDto, type HttpClient } from "@nasam/kernel";
import type {
  AssetAssignmentSortBy,
  AssetAssignmentsListQuery,
  AssetAssignmentsRepository,
} from "../domain";
import {
  assetAssignmentListResponseDtoSchema,
  assetAssignRequestDtoSchema,
  assetAssignResponseDtoSchema,
} from "./assetAssignmentDtos";
import { assetAssignmentFromDto } from "./assetAssignmentMappers";

const LIST_PATH = `${API_PREFIX}/assets/branch-assignments`;
const ASSIGN_PATH = `${API_PREFIX}/assets/branch-assignment`;

// The screen sorts the name columns per UI language; the API takes one name
// per column, and calls the assignment date createdAt.
const SORT_BY_TO_API: Record<AssetAssignmentSortBy, string> = {
  branchNameEn: "branchName",
  branchNameAr: "branchName",
  assetNameEn: "assetName",
  assetNameAr: "assetName",
  assignedAt: "createdAt",
};

function listParams(query: AssetAssignmentsListQuery) {
  const search = query.search?.trim();
  return {
    page: query.page,
    limit: query.limit,
    sort: query.sort,
    sortBy: query.sortBy === undefined ? undefined : SORT_BY_TO_API[query.sortBy],
    search: search === "" ? undefined : search,
    fromCreatedAt: query.fromAssignedAt,
    toCreatedAt: query.toAssignedAt,
  };
}

export function createAssetAssignmentsHttpRepository(
  http: HttpClient,
): AssetAssignmentsRepository {
  return {
    // NASAM-290. A client user sees their own account's assets, staff every
    // account's. Defaults (page 1, limit 10, createdAt desc) are the API's own.
    async list(query = {}) {
      const response = await http.get(LIST_PATH, { params: listParams(query) });
      return paginatedFromDto(
        assetAssignmentListResponseDtoSchema.parse(response),
        assetAssignmentFromDto,
      );
    },

    // NASAM-289. One transaction: every asset moves or none does. 400 (inactive
    // branch) / 404 (unknown asset or branch) / 409 (asset already in the
    // branch) propagate as HttpError carrying the backend's translated
    // message, which the app-wide mutation toast shows.
    async assign(input) {
      const response = await http.patch(
        ASSIGN_PATH,
        assetAssignRequestDtoSchema.parse({
          branchId: input.branchId,
          targetAssets: input.assetIds,
        }),
      );
      const parsed = assetAssignResponseDtoSchema.parse(response);
      return {
        assignments: parsed.data.map(assetAssignmentFromDto),
        message: parsed.message ?? null,
      };
    },
  };
}
